import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useForm } from 'react-hook-form';
import { ReactComponent as PlusIcon } from '@rocco/icons/svg/plus-no-padding.svg';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import {
    BuildHubProject,
    DesignBoardViewModel,
} from '@rocco/ui/design-board/view-models/design-board';
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
    useRef,
    useMemo,
} from 'react';
import { Switch } from '@rocco/components/switch';
import { Textarea } from '@rocco/components/textarea';
import { Input } from '@rocco/components/input';
import { Button } from '@rocco/components/button';
import { cn } from '@rocco/utils/cn';
import { Label } from '@rocco/components/label';
import { DesignBoardTile, DesignBoardTileSkeleton } from './DesignBoardTile';

import { CircleAlert } from 'lucide-react';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
} from '@rocco/components/drawer';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { useFeature } from '@archipro-website/web/app/modules/root';

export const uiStateAtomSelectDesignBoardModalIsOpened = atom(false);
export const uiStateAtomProcessingBoardIds = atom<number[]>([]);

interface SelectDesignBoardModalProps {
    displayMode: DisplayMode;
    allowCreateNewBoard: boolean;
    allowClickAndSelect: boolean;
    boards?: DesignBoardViewModel[];
    createdBoardData?: DesignBoardViewModel;
    createBoardError?: string | null;
    resetCreateBoardError?: () => void;
    onFetchBoards: () => void;
    onCreateBoard: (
        name: string,
        description?: string,
        isPrivate?: boolean,
        buildHubId?: number
    ) => void;
    onBoardSelected: (
        results: { board: DesignBoardViewModel; action: 'add' | 'remove' }[],
        isProjectBoard?: boolean
    ) => void;
    onCancel: () => void;
    selectedItems: number[];

    // mood boards
    moodBoards?: DesignBoardViewModel[];
    onFetchMoodBoards?: () => void;
}

export interface MoodBoardGroup {
    project: BuildHubProject;
    boards: DesignBoardViewModel[];
}

const DEFAULT_PROJECT_OPTION: BuildHubProject = {
    id: 0,
    title: 'Design Boards',
};

export const SelectDesignBoardModal = (props: SelectDesignBoardModalProps) => {
    const {
        displayMode,
        allowCreateNewBoard,
        allowClickAndSelect,
        boards,
        selectedItems,
        moodBoards,
        createdBoardData,
        createBoardError,
        resetCreateBoardError,
        onFetchBoards,
        onFetchMoodBoards,
        onCreateBoard,
        onBoardSelected,
        onCancel,
    } = props;

    const [
        uiStateSelectDesignBoardModalIsOpened,
        setUiStateSelectDesignBoardModalIsOpened,
    ] = useAtom(uiStateAtomSelectDesignBoardModalIsOpened);

    const isMobile = displayMode === DisplayMode.Mobile;

    const [isAddingNewBoard, setIsAddingNewBoard] = useState(false);
    const [selectedBoards, setSelectedBoards] = useState<
        DesignBoardViewModel[]
    >([]); // used for click and select mode
    const [initialSelectedBoards, setInitialSelectedBoards] = useState<
        DesignBoardViewModel[]
    >([]);
    const [activeProject, setActiveProject] = useState<BuildHubProject>(
        DEFAULT_PROJECT_OPTION
    );
    const formRef = React.useRef<{ submit: () => void }>(null);

    const isProjectBoard = activeProject.id !== DEFAULT_PROJECT_OPTION.id;

    const handleBoardSelect = useCallback(
        (board: DesignBoardViewModel) => {
            const isAlreadySelected = board.pins?.some(
                (pin) =>
                    pin.libraryItemId &&
                    selectedItems.includes(pin.libraryItemId)
            );

            const action = isAlreadySelected ? 'remove' : 'add';

            onBoardSelected([{ board, action }], isProjectBoard);
        },
        [onBoardSelected, selectedItems, isProjectBoard]
    );

    const handleMultipleBoardSelect = useCallback(
        (boards: DesignBoardViewModel[]) => {
            const initialBoardIds = new Set(
                initialSelectedBoards.map((b) => b.id)
            );
            const currentBoardIds = new Set(boards.map((b) => b.id));

            const addedBoards = boards
                .filter((b) => !initialBoardIds.has(b.id))
                .map((board) => ({ board, action: 'add' as const }));

            const removedBoards = initialSelectedBoards
                .filter((b) => !currentBoardIds.has(b.id))
                .map((board) => ({ board, action: 'remove' as const }));

            onBoardSelected([...addedBoards, ...removedBoards], isProjectBoard);
        },
        [onBoardSelected, initialSelectedBoards, isProjectBoard]
    );

    const handleBoardCreate = useCallback(
        (title: string, description: string, isPrivate: boolean) => {
            onCreateBoard(title, description, isPrivate, activeProject.id);
        },
        [onCreateBoard, activeProject]
    );

    /**
     * Handles auto-selection of newly created design board
     *
     * - Only triggers when createdBoardData exists and has valid ID > 0
     * - If createdBoardData is null/undefined, no action taken
     * - If createdBoardData.id <= 0, invalid ID, no action taken
     */
    useEffect(() => {
        if (createdBoardData && createdBoardData.id > 0) {
            onBoardSelected(
                [{ board: createdBoardData, action: 'add' }],
                isProjectBoard
            );
        }
    }, [createdBoardData, onBoardSelected, isProjectBoard]);

    /**
     * Handles modal open state changes
     *
     * - Only triggers when modal is opened (true)
     * - Resets "adding new board" state if active (assuming it's a just opened modal and we don't want to show the new board form)
     * - Resets "selected board" state if active (in case of click and select mode)
     * - No action taken when modal is closed (false)
     * - Prevents unnecessary board fetches when modal is already open
     */
    useEffect(() => {
        if (uiStateSelectDesignBoardModalIsOpened) {
            isAddingNewBoard && setIsAddingNewBoard(false);
            onFetchBoards();
            onFetchMoodBoards?.();
        } else {
            setSelectedBoards([]);
            setInitialSelectedBoards([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiStateSelectDesignBoardModalIsOpened]);

    const boardsAndMoodboards = useMemo(
        () => [...(boards ?? []), ...(moodBoards ?? [])],
        [boards, moodBoards]
    );
    const isInitialSelectionDone = useRef(false);

    useEffect(() => {
        if (uiStateSelectDesignBoardModalIsOpened) {
            isInitialSelectionDone.current = false;
        }
    }, [uiStateSelectDesignBoardModalIsOpened]);

    useEffect(() => {
        // Wait until all data we need is loaded:
        // - If mood boards are used, wait for BOTH boards and moodBoards to be defined
        // - Otherwise just wait for boards
        const needsMoodBoards = typeof onFetchMoodBoards === 'function';
        const dataReady =
            boards !== undefined &&
            (!needsMoodBoards || moodBoards !== undefined);

        if (
            !isInitialSelectionDone.current &&
            uiStateSelectDesignBoardModalIsOpened &&
            dataReady
        ) {
            const allAvailableBoards = boardsAndMoodboards;
            const uniqueBoards = allAvailableBoards.length
                ? Array.from(
                      new Map(allAvailableBoards.map((b) => [b.id, b])).values()
                  )
                : [];

            const initiallySelected = uniqueBoards.filter((board) =>
                board.pins?.some(
                    (pin) =>
                        pin.libraryItemId &&
                        selectedItems.includes(pin.libraryItemId)
                )
            );

            if (allowClickAndSelect) {
                setInitialSelectedBoards(initiallySelected);
                setSelectedBoards(initiallySelected);
            }

            // mark as done after we considered all currently available boards
            isInitialSelectionDone.current = true;
        }
    }, [
        uiStateSelectDesignBoardModalIsOpened,
        boards,
        moodBoards,
        boardsAndMoodboards,
        allowClickAndSelect,
        selectedItems,
        onFetchMoodBoards,
    ]);

    const isLoading =
        boards === undefined ||
        (onFetchMoodBoards !== undefined && moodBoards === undefined);
    const projectSelectorOptions = useMemo(() => {
        return moodBoards ? groupMoodBoardsByProjectName(moodBoards) : [];
    }, [moodBoards]);
    const buildHubEnabled = useFeature('buildhub');
    const showProjectSelector =
        buildHubEnabled && projectSelectorOptions.length > 1;

    const boardsToDisplay =
        activeProject.id === DEFAULT_PROJECT_OPTION.id
            ? boards
            : projectSelectorOptions.find(
                  (group) => group.project.id === activeProject.id
              )?.boards;

    // preselect first project
    const hasAutoSelected = useRef(false);
    useEffect(() => {
        if (!hasAutoSelected.current && projectSelectorOptions.length > 1) {
            const firstProject = projectSelectorOptions[0]?.project;
            if (firstProject) {
                setActiveProject(firstProject);
            }
            hasAutoSelected.current = true;
        }
    }, [projectSelectorOptions]);

    const drawerSelectTitle = showProjectSelector
        ? 'Select Project or Design Board'
        : 'Select Design Board';
    const drawerCreateTitle = showProjectSelector
        ? `New ${isProjectBoard ? 'Mood Board' : 'Design Board'}`
        : 'New Design Board';

    const haveSelectionsChanged = useMemo(() => {
        const initialIds = new Set(initialSelectedBoards.map((b) => b.id));
        const currentIds = new Set(selectedBoards.map((b) => b.id));

        if (initialIds.size !== currentIds.size) {
            return true;
        }

        for (const id of initialIds) {
            if (!currentIds.has(id)) {
                return true;
            }
        }

        return false;
    }, [initialSelectedBoards, selectedBoards]);

    return (
        <Drawer
            handleOnly={!isMobile}
            open={uiStateSelectDesignBoardModalIsOpened}
            onOpenChange={setUiStateSelectDesignBoardModalIsOpened}
            repositionInputs={false}
        >
            <DrawerContent
                variant="glassy"
                overlayVariant="dark"
                noHandle={!isMobile}
                hideDefaultCloseButton={isMobile}
                limitMaxHeight={isMobile}
                className={`h-full md:h-[36.5rem]`}
            >
                <div className="flex h-full w-full flex-col">
                    <DrawerHeader variant="border" alignment="left">
                        <DrawerTitle>
                            {isAddingNewBoard
                                ? drawerCreateTitle
                                : drawerSelectTitle}
                        </DrawerTitle>
                        {/* Suppress missing description console warning */}
                        <DrawerDescription className="hidden" aria-hidden />
                        {showProjectSelector && (
                            <div className="w-full">
                                <Select
                                    value={activeProject.id.toString()}
                                    onValueChange={(value) => {
                                        const selected =
                                            projectSelectorOptions.find(
                                                (group) =>
                                                    group.project.id.toString() ===
                                                    value
                                            );
                                        if (selected) {
                                            setActiveProject(selected.project);
                                        }
                                    }}
                                >
                                    <SelectTrigger
                                        className="w-full"
                                        color="gray"
                                    >
                                        <SelectValue placeholder="Select a project">
                                            {activeProject?.title}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {projectSelectorOptions.map((group) => (
                                            <SelectItem
                                                key={group.project.id}
                                                value={group.project.id.toString()}
                                            >
                                                {group.project.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </DrawerHeader>
                    <div
                        className={cn(
                            'scrollbar-hidden w-dvw flex-1 overflow-y-scroll md:w-[56.125rem] md:flex-auto md:px-6',
                            isAddingNewBoard
                                ? 'md:h-[21.75rem]'
                                : 'md:h-[26.5rem]'
                        )}
                    >
                        {isAddingNewBoard ? (
                            <AddNewBoard
                                onCreateNewBoard={handleBoardCreate}
                                formRef={formRef}
                                isProjectBoard={isProjectBoard}
                                generalError={createBoardError}
                            />
                        ) : (
                            <SelectDesignBoard
                                boards={boardsToDisplay}
                                isLoading={isLoading}
                                allowClickAndSelect={allowClickAndSelect}
                                selectedBoards={selectedBoards}
                                selectedItems={selectedItems}
                                setSelectedBoards={setSelectedBoards}
                                allowCreateNewBoard={allowCreateNewBoard}
                                onCreateNewBoard={() => {
                                    // Clear any existing error when switching to add new board
                                    resetCreateBoardError?.();
                                    setIsAddingNewBoard(true);
                                }}
                                onBoardSelected={handleBoardSelect}
                            />
                        )}
                    </div>

                    {isAddingNewBoard ? (
                        <DrawerFooter>
                            <Button
                                size="xLarge"
                                color={
                                    displayMode === DisplayMode.Mobile
                                        ? 'outline'
                                        : 'secondary'
                                }
                                minWidth="200"
                                onClick={() =>
                                    isAddingNewBoard
                                        ? setIsAddingNewBoard(false)
                                        : onCancel()
                                }
                            >
                                <span className="text-lg">Cancel</span>
                            </Button>
                            <Button
                                size="xLarge"
                                minWidth="200"
                                onClick={() => formRef.current?.submit()}
                            >
                                <span className="text-lg">Save</span>
                            </Button>
                        </DrawerFooter>
                    ) : (
                        allowClickAndSelect && (
                            <DrawerFooter>
                                <Button
                                    size="xLarge"
                                    minWidth="200"
                                    disabled={!haveSelectionsChanged}
                                    onClick={() =>
                                        haveSelectionsChanged &&
                                        handleMultipleBoardSelect(
                                            selectedBoards
                                        )
                                    }
                                >
                                    <span className="flex items-center justify-between gap-2 text-lg">
                                        <span>Save</span>
                                        <ArrowRightIcon className="size-4.5" />
                                    </span>
                                </Button>
                            </DrawerFooter>
                        )
                    )}
                </div>
            </DrawerContent>
        </Drawer>
    );
};

///// Content (Select Design Board) /////

interface SelectDesignBoardProps {
    allowClickAndSelect: boolean;
    selectedBoards: DesignBoardViewModel[];
    setSelectedBoards: (boards: DesignBoardViewModel[]) => void;
    boards?: DesignBoardViewModel[];
    isLoading: boolean;
    allowCreateNewBoard: boolean;
    onCreateNewBoard: () => void;
    onBoardSelected: (board: DesignBoardViewModel) => void;
    selectedItems: number[];
}

const SelectDesignBoard = (props: SelectDesignBoardProps) => {
    const {
        boards,
        isLoading,
        allowCreateNewBoard,
        onCreateNewBoard,
        onBoardSelected,
        allowClickAndSelect,
        selectedBoards,
        setSelectedBoards,
        selectedItems,
    } = props;
    const processingBoardIds = useAtomValue(uiStateAtomProcessingBoardIds);

    const handleBoardClick = (boardId: number) => {
        const board = boards?.find((b) => b.id === boardId);
        if (board) {
            if (allowClickAndSelect) {
                const isSelected = selectedBoards.some((b) => b.id === boardId);
                if (isSelected) {
                    setSelectedBoards(
                        selectedBoards.filter((b) => b.id !== boardId)
                    );
                } else {
                    setSelectedBoards([...selectedBoards, board]);
                }
            } else {
                onBoardSelected(board);
            }
        }
    };

    return (
        <div className="grid select-none grid-cols-2 gap-x-2 gap-y-6 px-2 py-[1.1875rem] md:grid-cols-3 md:gap-y-12 md:px-0 md:py-8">
            {!isLoading && allowCreateNewBoard && (
                <SelectDesignBoardModalNewBoardButton
                    onCreateNewBoard={onCreateNewBoard}
                />
            )}

            {isLoading
                ? [0, 1, 2].map((index) => (
                      <DesignBoardTileSkeleton key={index} />
                  ))
                : boards?.map((board) => {
                      const isSelected = allowClickAndSelect
                          ? selectedBoards.some((b) => b.id === board.id)
                          : board.pins?.some(
                                (pin) =>
                                    pin.libraryItemId &&
                                    selectedItems &&
                                    selectedItems.includes(pin.libraryItemId)
                            );
                      const isProcessing = processingBoardIds.includes(
                          board.id
                      );

                      return (
                          <DesignBoardTile
                              key={board.id}
                              board={board}
                              isSelected={!!isSelected && !isProcessing}
                              isLoading={isProcessing}
                              showHoverButton={!allowClickAndSelect}
                              buttonText="Save"
                              onTileClick={handleBoardClick}
                          />
                      );
                  })}
        </div>
    );
};

///// Content (Add New Board) /////

interface AddNewBoardProps {
    onCreateNewBoard: (
        title: string,
        description: string,
        isPrivate: boolean
    ) => void;
    formRef?: React.RefObject<{ submit: () => void }>;
    isProjectBoard?: boolean;
    generalError?: string | null;
}

const AddNewBoard = (props: AddNewBoardProps) => {
    const { onCreateNewBoard, isProjectBoard, generalError } = props;

    const formSchema = z.object({
        title: z.string().min(1, 'Title is required'),
        description: z.string().optional(),
        isPrivate: z.boolean(),
    });

    type FormValues = z.infer<typeof formSchema>;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: '',
            description: '',
            isPrivate: false,
        },
    });

    const onSubmit = (values: FormValues) => {
        onCreateNewBoard(
            values.title,
            values.description ?? '',
            !!values.isPrivate
        );
    };

    useImperativeHandle(props.formRef, () => ({
        submit: () => form.handleSubmit(onSubmit)(),
    }));

    return (
        <div className="w-full px-6 py-8 md:px-0">
            <Form {...form}>
                <form
                    onSubmit={() => form.handleSubmit(onSubmit)}
                    className="space-y-8"
                >
                    {/* Display server-side error messages */}
                    {generalError && (
                        <p
                            className={
                                'rocco-form-message text-sm text-red-700'
                            }
                        >
                            <span className="flex items-center gap-1">
                                <CircleAlert className="size-3" />
                                <span>{generalError}</span>
                            </span>
                        </p>
                    )}
                    <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>
                                    <span className="text-base font-medium leading-tight">
                                        *{' '}
                                        {isProjectBoard
                                            ? 'Mood Board title'
                                            : 'Design Board title'}
                                    </span>
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        variant="small"
                                        color="contrast"
                                        text="default"
                                        type="text"
                                        placeholder="My First Design Board"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>
                                    <span className="text-base font-medium leading-tight">
                                        Description
                                    </span>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        color="contrast"
                                        placeholder="Enter Description"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="isPrivate"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="-mt-2 flex items-center gap-2">
                                        <Switch
                                            id="private-board"
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                        <Label
                                            htmlFor="private-board"
                                            text="medium"
                                        >
                                            Private Board
                                        </Label>
                                    </div>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </form>
            </Form>
        </div>
    );
};

///// New Board Button /////

interface SelectDesignBoardModalNewBoardButtonProps {
    onCreateNewBoard: () => void;
}

const SelectDesignBoardModalNewBoardButton = (
    props: SelectDesignBoardModalNewBoardButtonProps
) => {
    const { onCreateNewBoard } = props;

    return (
        <button
            onClick={onCreateNewBoard}
            className="flex aspect-[4/2.9] w-full flex-col items-center justify-start rounded-lg border border-dashed border-gray-900 transition-colors hover:border-gray-900/90 md:aspect-[4/3]"
        >
            <div className="mt-10 flex size-6 items-center justify-center text-gray-500 md:mt-14">
                <PlusIcon />
            </div>
            <span className="mt-8.5 text-sm text-gray-900">New Board</span>
        </button>
    );
};

export const groupMoodBoardsByProjectName = (
    moodBoards: DesignBoardViewModel[]
): MoodBoardGroup[] => {
    const grouped = moodBoards.reduce<
        Map<
            number,
            { project: BuildHubProject; boards: DesignBoardViewModel[] }
        >
    >((acc, board) => {
        const project = board.project;
        if (!project) return acc;
        if (!acc.has(project.id)) {
            acc.set(project.id, { project, boards: [] });
        }

        acc.get(project.id)!.boards.push(board);
        return acc;
    }, new Map());

    const groups = Array.from(grouped.values());

    groups.push({ project: DEFAULT_PROJECT_OPTION, boards: [] });

    return groups;
};
